import { RootStore } from 'store/rootStore';
import { makeAutoObservable, runInAction } from 'mobx';
import { FullUser, SEUser, UserType } from 'types/userService';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getEmployersForGroup, inviteToSelfEnroll, removeEmployer, updateEmployer } from 'api/operations/userService';
import { Primitive } from 'types/general';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';

export interface FormFields extends DefaultFormFields {
  firstName: FormControl<string>;
  middleName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  telephone: FormControl<string>;
  title: FormControl<string>;
}

export class AdditionalAccessStore {
  rootStore: RootStore;
  groupEmployers: FullUser[] = [];
  loading = false;
  isEditEmployersModalOpen = false;
  addEmployerForm: FormController<FormFields>;
  editEmployerForm: FormController<FormFields>;
  isEditFormOpen = false; // trigger edit form open status.

  public setLoading(status: boolean): void {
    this.loading = status;
  }

  public setEditFormStatus(status: boolean): void {
    this.isEditFormOpen = status;
  }

  public async getEmployersForGroup(groupId: string): Promise<void> {
    this.setLoading(true);
    const employers = await getEmployersForGroup(groupId);
    runInAction(() => {
      this.groupEmployers = employers;
      this.setLoading(false);
    });
  }

  public openEditEmployerForm(employerId: string): void {
    this.setEditFormStatus(true);
    const currentEmployer = this.groupEmployers.find(({ id }) => id === employerId);
    if (currentEmployer) {
      this.editEmployerForm.populateFields(currentEmployer as unknown as Record<string, Primitive>);
    }
  }

  public openEditEmployersModal(): void {
    this.isEditEmployersModalOpen = true;
  }

  public closeEditEmployersModal(): void {
    this.setEditFormStatus(false);
    this.isEditEmployersModalOpen = false;
    this.addEmployerForm.resetForm();
  }

  public async addEmployer(groupId: string): Promise<void> {
    this.setLoading(true);

    const data = this.addEmployerForm.getFormDataObject();
    const newEmployerInfo = {
      ...data,
      groupId,
      userType: 'EMPLOYER' as UserType,
    };

    const newEmployers = await inviteToSelfEnroll([newEmployerInfo as SEUser]);
    runInAction(() => {
      this.groupEmployers = [...this.groupEmployers, ...newEmployers];
    });
    this.setEditFormStatus(false);
    this.addEmployerForm.resetForm();
    this.setLoading(false);
  }

  public async editEmployerInfo(employerId: string, groupId: string, isGroupAdmin: boolean): Promise<void> {
    this.setLoading(true);
    const updatedEmployerInfo = {
      firstName: this.editEmployerForm.fields.firstName.value,
      middleName: this.editEmployerForm.fields.middleName.value,
      lastName: this.editEmployerForm.fields.lastName.value,
      telephone: this.editEmployerForm.fields.telephone.value,
      title: this.editEmployerForm.fields.title.value,
      id: employerId,
      groupId,
    };

    const updatedEmployer = await updateEmployer(updatedEmployerInfo);
    runInAction(() => {
      const updatedEmployersList = this.groupEmployers.map((employerInfo) => {
        if (employerInfo.id === employerId) return updatedEmployer;
        return employerInfo;
      });
      this.groupEmployers = updatedEmployersList;
      if (isGroupAdmin) {
        // Update the group's contact info in the domain store when editing a group admin.
        // This is a temporary workaround until the new group profile is created.
        void this.rootStore.groupStore.updateGroup(
          buildInputGroupFromGroup({
            ...this.rootStore.groupStore.group,
            contactInfo: {
              ...this.rootStore.groupStore.group.contactInfo,
              firstName: updatedEmployerInfo.firstName,
              lastName: updatedEmployerInfo.lastName,
              primaryPhone: updatedEmployerInfo.telephone,
              title: updatedEmployerInfo.title,
            },
          }),
        );
      }
    });
    this.setEditFormStatus(false);
    this.setLoading(false);
  }

  public removeEmployer(groupId: string, employerId: string, isGroupAdmin: boolean): void {
    void removeEmployer(groupId, employerId);
    runInAction(() => {
      const filteredEmployers = this.groupEmployers.filter(({ id }) => id !== employerId);
      this.groupEmployers = filteredEmployers;

      if (isGroupAdmin) {
        // Update the group's contact info in the domain store when editing a group admin.
        // This is a temporary workaround until the new group profile is created.
        void this.rootStore.groupStore.updateGroup(
          buildInputGroupFromGroup({
            ...this.rootStore.groupStore.group,
            contactInfo: {
              firstName: '',
              lastName: '',
              primaryPhone: '',
              primaryEmail: '',
              title: '',
              primaryAddress: {},
            },
          }),
        );
      }
    });
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const generateForm = (): FormFields => {
      return {
        firstName: new FormControl<string>('', { validations: ['validName'], trim: true }),
        middleName: new FormControl<string>('', { validations: ['validName'], optional: true, trim: true }),
        lastName: new FormControl<string>('', { validations: ['validName'], trim: true }),
        email: new FormControl<string>('', { validations: ['isEmailValid'], trim: true }),
        telephone: new FormControl<string>('', { validations: ['isValidUSAPhoneNumber'], optional: true }),
        title: new FormControl<string>('', { validations: ['validName'], optional: true }),
      };
    };

    this.addEmployerForm = new FormController(generateForm());
    this.editEmployerForm = new FormController(generateForm());

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
